// ===============================
// DISTRICTS
// ===============================
// The districts a map is laid out in, and the only place their names are
// written down. A cell's district is a property of the CELL — `room.zone` — and
// of nothing else; a room template says which district it would rather stand in
// with `districtAffinity`, which is a different question and now has a
// different name for it. The two were both called `zone`, which is how the map
// came to have an invisible district structure that agreed with nothing.
//
// No `transit` district. Roads are already a layer of their own — `room.road`,
// `roadRank`, `roadDist` — and a transit district is the same claim made twice
// by two systems that will disagree about where it is. Its entry in the old
// preferences table was, tellingly, empty.
// `recipes` is what a district is BUILT of: the mix of tile art its ordinary
// cells are drawn from, weighted, summing to 100 so the numbers read as
// percentages. It is what makes a district look like one.
//
// It is deliberately separate from which ROOM stands on a cell. A region owns
// twelve room templates for a map of up to 119 cells, so each is reused eight
// to eleven times — far too few to build a district out of. Picking rooms by
// district instead would collapse a residential district onto the one
// residential template, testing the one skill it carries. So the district
// decides the building type, the room keeps its own name, category and skill,
// and the skill anchor already drawn on every encounter tile says what is
// actually being attempted there. The recipe says where you are; the anchor
// says what you are doing; the name says what it is called.
//
// Every district leads on one recipe at around half its cells, which is what
// makes it recognisable, and carries three or four others so it does not read
// as a monoculture.
// `built` is how densely a district is built up, 0..1, and the renderer reads
// it directly: it thins a tile's structures, and inversely lets the growth back
// in and the tarmac go to seed. Authored rather than sampled from noise so a
// district's look and its name agree — an outskirt is thin because it IS the
// outskirts, not because a lattice happened to dip there.
//
// The numbers are centred on MEAN_BUILT (roomImageGen) so the average tile
// lands where it already was. This redistributes built stock across the map; it
// does not delete it, and there is a test holding the total to within a tenth.
export const zoneArchetypes = {
  entry: {
    description: "Starting area with easier encounters",
    recipes: { infrastructure: 35, transit: 25, security: 20, storage: 20 },
    built: 0.72,
  },
  peripheral: {
    description: "Outer areas with mixed encounters",
    recipes: { environmental: 40, transit: 20, storage: 20, habitation: 20 },
    built: 0.58,
  },
  security: {
    description: "Defensive checkpoints",
    recipes: { security: 45, infrastructure: 25, storage: 20, power: 10 },
    built: 0.90,
  },
  industrial: {
    description: "Resource/infrastructure areas",
    recipes: { power: 30, storage: 30, infrastructure: 25, transit: 15 },
    built: 0.88,
  },
  core: {
    description: "High-value protected areas",
    recipes: { records: 35, commerce: 25, security: 20, infrastructure: 20 },
    built: 1.00,
  },
  residential: {
    description: "Habitats, apartments, communal living spaces",
    recipes: { habitation: 50, environmental: 20, records: 15, commerce: 15 },
    built: 0.85,
  },
  commercial: {
    description: "Shops, offices, market halls, logistics fronts",
    recipes: { commerce: 50, storage: 20, records: 15, habitation: 15 },
    built: 0.95,
  },
};

// ===============================
// COMMON ROOMS
// ===============================
export const commonAreas = {
  campsite: {
    type: "campsite",
    name: "Campsite",
    primary: "",
    support: [],
    baseDuration: 120,
    icon: "🏕️",
    description: "A safe place to rest and recover",
    healingAmount: 30,
  },
  water: {
    type: "water",
    name: "Water",
    primary: "",
    support: [],
    baseDuration: 120,
    icon: "🌊",
    description: "A water source for fishing",
  },
  road: {
    type: "road",
    name: "Road",
    primary: "",
    support: [],
    baseDuration: 10,
    icon: "🛣️",
    description: "A roadway for fast travel",
  },
};

// raidRegions.js
// Regions data with synchronized time-of-day and clarified lighting.
// Square, pixel-art oriented image prompts; natural/team lighting only.
// Adds: “Explorers’ handheld lights only; no street or facility lighting.”
// Includes shortDescription and longDescription on regions, gates, and hazards.

export const raidRegions = {
  // ======================================================
  "shard-cities": {
    name: "Shard Cities",
    theme: "Collapsed megacities and fractured districts",
    icon: "🏙️",
    shortDescription:
      "Towering urban ruins split by broken transit lines and silent civic grids.",
    longDescription:
      "These were once continuous skylines: stacked transit, dense housing, and civic utilities woven into mile-high towers. Centuries of neglect cracked the pavement and snapped the skyways. You can still read the street plan, even if whole blocks have slumped into their own debris.\n\nPower occasionally surges through old feeders, enough to light a dead intersection for a breath. Most systems do nothing at all—just rust, dust, and old signage pointing nowhere.",
    artDirection:
      "Two scales of building, and the contrast between them IS the region. The ordinary stock is warm — brick and brown roofing, low, dense, packed kerb to kerb. Over it stand cool concrete-and-glass masses: tower cores with their service shafts open to the sky, skybridge decks that start nowhere and stop in mid-air, sign gantries still spanning the carriageway. Ground is dark slate tarmac, cracked on a slab pitch, so both tiers read light against it. Street trees have come up through the pavement everywhere there was a cutout.",
    gates: [
      {
        icon: "🚧",
        name: "Checkpoint Sector",
        primary: "lock-breach",
        support: ["signal-relay", "stealth-transit"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "critical",
        shortDescription: "Barricaded intersection controlling movement between ruined blocks.",
        longDescription:
          "Concrete barriers, rusted railings, and makeshift roadblocks choke an old arterial. The city’s last attempts at order still shape the flow of rubble and weeds. Nothing powers here—only wind and dust."
      },
      {
        icon: "⚙️",
        name: "Civic Relay Block",
        primary: "signal-relay",
        support: ["quantum-scan", "lock-breach"],
        baseDuration: 120,
        category: "infrastructure",
        difficulty: "chokepoint",
        placement_priority: "high",
        shortDescription: "Collapsed service block with dead comms towers and trunk conduits.",
        longDescription:
          "Stubbed antenna masts lean over a plaza of broken panels and cable vaults. Dust-muted signage points to services that no longer exist. A faint click from a relay box sometimes answers fresh power before falling silent again."
      },
      {
        icon: "🏢",
        name: "Apartment Access Lot",
        primary: "stealth-transit",
        support: ["quantum-scan", "lock-breach"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Collapsed housing block entrances clustered around a cracked forecourt.",
        longDescription:
          "Entry canopies hang at angles over door stacks long since jammed. Mailboxes gape, names scoured to metal. The forecourt is a map of fractures and weeds, with scaffolds rusted into stillness."
      },
      {
        icon: "🛍️",
        name: "Arcade Shutter Sector",
        primary: "system-override",
        support: ["drone-command", "containment"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Market frontage sealed by fallen shutters and buckled grates.",
        longDescription:
          "A run of storefronts slouches under dead signage and twisted awnings. Steel shutters are half-dropped or jammed, creating a maze of narrow access. Any movement here is the wind through canvas and chain."
      }
    ],
    hazards: [
      {
        icon: "🛢️",
        name: "Residential Power Yard",
        primary: "containment",
        support: ["signal-relay", "transit-anchor"],
        baseDuration: 60,
        category: "power",
        difficulty: "standard",
        placement_priority: "high",
        shortDescription: "Small substation between towers with fallen lines and rusted switchgear.",
        longDescription:
          "Transformer housings slouch on cracked pads; the fence is more gap than mesh. A wrong step crunches glass and ceramic. If anything lights, it’s for a breath, then the quiet deepens."
      },
      {
        icon: "🌉",
        name: "Transit Collapse Zone",
        primary: "transit-anchor",
        support: ["reclamation", "system-override"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Broken overpass and skybridge stubs sprawled over a debris-choked road.",
        longDescription:
          "Guardrails spear the pavement; cables drape from shattered spans. The echo under concrete ribs tells how big it was. Weeds stitch rebar and dust into something like ground again."
      },
      {
        icon: "📦",
        name: "Marketfront Depot",
        primary: "drone-command",
        support: ["transit-anchor", "echo-trace"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        shortDescription: "Cluster of loading bays and shuttered storerooms behind ruined shops.",
        longDescription:
          "Dock plates sit crooked; pallets rot into dark lines on the ground. The loading lights are dead—only daylight finds its way in through gaps and broken louvers."
      },
      {
        icon: "🏢",
        name: "Administrative Records Block",
        primary: "quantum-scan",
        support: ["system-override", "lock-breach"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "critical",
        shortDescription: "Abandoned office district with shattered facades and paper-littered streets.",
        longDescription: "Stacks of files turned to mulch line the curbs where floodwater once ran. A rusted maintenance crane leans against a curtain wall. Nothing watches here—only glass dust and wind."
      },
      {
        icon: "🚉",
        name: "Transit Exchange Block",
        primary: "echo-trace",
        support: ["stealth-transit", "containment"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "A surface-level transit interchange and service plaza linking several collapsed lines.",
        longDescription: "Once a hub for elevated trams and vertical shuttles, the block still shows the scar of its platforms. Canopies hang over cracked concrete lanes, and access ramps descend into dust-filled tunnels below."
      },
      {
        icon: "🚛",
        name: "Logistics Yard Ruins",
        // overturned cargo trucks and container stacks, not a service yard
        recipe: "storage",
        primary: "reclamation",
        support: ["drone-command", "echo-trace"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Collapsed distribution yard filled with overturned cargo trucks and container stacks.",
        longDescription: "What was once a multi-lane loading grid now lies in layers — trucks fossilized in place, containers torn open by time. Tower cranes stand idle at the corners like monuments to motionless trade."
      },
      {
        icon: "⚡",
        name: "Substation Complex",
        primary: "system-override",
        support: ["reclamation", "quantum-scan"],
        baseDuration: 60,
        category: "power",
        difficulty: "high_value",
        placement_priority: "high",
        shortDescription: "A cluster of transformers and conduit fields forming a decommissioned power block.",
        longDescription: "Heavy transformer shells sit on cracked pads, their housings marked with long-gone warning paint. Busbars bridge between fenced sections, while cable vaults yawn open into the dark below."
      },
      {
        icon: "🛂",
        name: "Civic Control District",
        // a ring of checkpoints; "civic" pulls it to infrastructure otherwise
        recipe: "security",
        primary: "quantum-scan",
        support: ["transit-anchor", "echo-trace"],
        baseDuration: 60,
        category: "security",
        difficulty: "standard",
        placement_priority: "high",
        shortDescription: "A cluster of interconnected checkpoints surrounding a former civic administration block.",
        longDescription: "Security kiosks ring the perimeter of a low central building, their scanner frames bent but still standing. The entire block was once a controlled corridor — a mix of barriers, scanners, and causeways now frozen in collapse."
      }      
    ]
  },

  // ======================================================
  "verdant-reclamation": {
    name: "Green Reclamation",
    theme: "Overgrown research campuses and greenhouse grids",
    icon: "🌿",
    shortDescription:
      "Bio-reclaimed districts where labs and irrigation frames disappear beneath growth.",
    longDescription:
      "This landscape was engineered to coax life back into sterile soil. Greenhouses formed shining corridors; irrigation lattices pulsed under glass. After the departure, containment failed and nature took the reins.\n\nNow, fibrous canopies bind whole blocks together. Steel trusses wear bark; road paint lies under moss. Machinery answers only sometimes, a pump stuttering once before the vines hold it still again.",
    artDirection:
      "Growth is the loudest thing on the tile and the buildings are what it grew over. Heavy canopy in deep greens, moss and wildflowers through the ground, tarmac worn back to soil. The architecture that survives is glasshouse: long shallow glazed spans ridged down their spine with panes missing and the planting showing through, irrigation lattices over what used to be beds, seed silos. Ordinary buildings are muted steel grey and mostly hidden — that is the point, they lost.",
    gates: [
      {
        icon: "🧪",
        name: "Bio-Lab Complex Gate",
        primary: "bio-shield",
        support: ["containment", "growth-control"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "critical",
        shortDescription: "Collapsed research compound entrance tangled in vines and glass.",
        longDescription:
          "A trellis of bent greenhouse ribs leads into a court of moss and broken tiles. Safety placards lie sun-bleached among seed trays and cracked tanks. The air is warm and wet, heavy with plant breath."
      },
      {
        icon: "🚿",
        name: "Irrigation Control Sector",
        primary: "reclamation",
        support: ["growth-control", "wild-influence"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        shortDescription: "Valve fields, cisterns, and control kiosks overtaken by roots.",
        longDescription:
          "Pipes run like exposed roots along the ground, bolted kiosks eaten by rust and kudzu. A cistern lid thumps underfoot—water still trapped below. If anything moves, it’s the wind dragging leaves across metal."
      },
      {
        icon: "🏚️",
        name: "Habitation Annex Gate",
        primary: "wild-influence",
        support: ["bio-shield", "reclamation"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Apartment annex entrances swallowed by creepers and fallen trellis.",
        longDescription:
          "Balconies have become planters; stairwells bloom with ferns. Doorways stand open or warped shut, their seals crumbled to powder. A single gutter still drips into a barrel of green water."
      },
      {
        icon: "🧪",
        name: "Market Decontamination Sector",
        primary: "containment",
        support: ["safeguard", "reclamation"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Outdoor wash stations and shuttered stalls clustered around a drained basin.",
        longDescription:
          "Decon arches frame a plaza of cracked tile and dried residue. The stalls around it are skeletal, their tarps long rotted away. Nothing cycles here unless you bring the power yourself."
      }
    ],
    hazards: [
      {
        icon: "🌱",
        name: "Seed Distribution Yard",
        primary: "growth-control",
        support: ["bio-shield", "reclamation"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        shortDescription: "Silos, bins, and pallet stacks buried in moss and husks.",
        longDescription:
          "A drift of cracked pods crunches underfoot. Stenciled measurements fade from the silo sides. Somewhere a vent exhales warm, earthy air that smells like the start of rain."
      },
      {
        icon: "🍄",
        name: "Fungal Spread Block",
        primary: "growth-control",
        support: ["bio-shield", "reclamation"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "A shaded block carpeted with fungus and spore fans.",
        longDescription:
          "Stems as thick as cable push through fissures in the concrete. Spores hang in the air like dust motes in a cathedral. Your breath fogs just enough to make the light feel heavy."
      },
      {
        icon: "💧",
        name: "Irrigation Breach Sector",
        primary: "bio-shield",
        support: ["growth-control", "safeguard"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Ruptured mains flooding a low lot, roots drinking the overflow.",
        longDescription:
          "A trench of clear water has cut through soil and tile, pooling around toppled control posts. Algae paints the edges; insects tap the surface. The flow wanes and returns without reason."
      },
      {
        icon: "📚",
        name: "Research Supply Yard",
        primary: "reclamation",
        support: ["wild-influence", "safeguard"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "critical",
        shortDescription: "Crated lab gear and broken cages stacked beside a ruined annex.",
        longDescription:
          "Numbers stenciled on crates tell of a mission left unfinished. Metal shelving slumps like wet paper. A single rain gutter ticks as it cools in the shade."
      },
      {
        icon: "🏗️",
        name: "Habitation Block",
        primary: "wild-influence",
        support: ["containment", "growth-control"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "A cluster of low-rise apartments overtaken by gardens and nests.",
        longDescription:
          "Bedsprings and trellis wire bind stairwells into new shapes. Window boxes have become small forests. The only movement is leaves, and the small quick things that live in them."
      },
      {
        icon: "🫗",
        name: "Nutrient Line Rupture",
        primary: "bio-shield",
        support: ["wild-influence", "safeguard"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Leaking feed lines turning a block into a slick, humming wetland.",
        longDescription:
          "Plastic tubing loops across the ground like shed skin, seeping a sweet, metallic film. New shoots force through asphalt in a straight line where the pipe runs below."
      },
      {
        icon: "🏪",
        name: "Agri-Market Depot",
        primary: "growth-control",
        support: ["containment", "bio-shield"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Open-air market storage with collapsed canopies and crate rows.",
        longDescription:
          "Sun-faded tarps hang like old flags. The smell of dust and seed oil lingers. You can still see where chalk mapped out stall fees on the concrete."
      },
      {
        icon: "🧯",
        name: "Greenhouse Containment Sector",
        primary: "safeguard",
        support: ["containment", "wild-influence"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "commercial",
        shortDescription: "Firebreak lanes and isolation frames choking in vines.",
        longDescription:
          "White-painted fire lanes vanish under creepers. The shutters meant to fall in emergencies are rusted half-down. A distant pane taps in the breeze like a metronome."
      }
    ]
  },

  // ======================================================
  "sunken-bastions": {
    name: "Flooded Bastions",
    theme: "Semi-submerged fortifications and silted logistics",
    icon: "🌊",
    shortDescription:
      "Defense districts with the sea in them: the coast took the low blocks and the silt took the rest.",
    longDescription:
      "Coastal defenses once ringed this region with docks, bunkers, and pressure locks. Storm surge and slow subsidence rolled in over centuries, flooding the low blocks and salting everything else.\n\nSome pressure doors still cycle if you give them power, drawing a final gulp of air through corroded ducts. Mostly, there is water, silt, and the sound of old metal moving in the tide.",
    artDirection:
      "One edge of the map is open ocean and everything reads off how far it is from it. At the shore, water over the outer half of the tile, a buttressed sea wall along the waterline with its bollards behind, and what is left of the buildings is mostly wreckage in the shallows. A row in, tidal flats and standing water pooling in the streets first. Beyond that, dry. Ground is cold silt, not sand — blue-grey, damp-looking, tide-marked. Concrete is wet and weeping rust.",
    gates: [
      {
        icon: "🛑",
        name: "Pressure Gate Sector",
        primary: "lock-breach",
        support: ["signal-mask", "wild-influence"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        shortDescription: "Cluster of rusted airlock doors and corroded valves around a flooded court.",
        longDescription:
          "Door rings sit proud with barnacle rust; inspection windows are opaque with salt. The control pedestals are still in place, their labels sanded flat by wind and spray."
      },
      {
        icon: "⚓",
        name: "Drydock Access Block",
        primary: "lock-breach",
        support: ["quantum-scan", "system-override"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "critical",
        shortDescription: "Collapsed dock gates and winch frames guarding entry to an old basin.",
        longDescription:
          "The dock walls lean inward, heavy with waterlogged stone. Winch cables coil like sleeping eels beside shattered bollards. Every surface is stained a dozen kinds of orange."
      },
      {
        icon: "🚪",
        name: "Residential Seal Lot",
        primary: "containment",
        support: ["safeguard", "system-override"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Pressure-rated housing entries scattered along a raised walkway.",
        longDescription:
          "Doors that once sealed against storms now gape, warped on their hinges. A painted muster diagram flakes off the wall in curls as thin as fish scales."
      },
      {
        icon: "🧳",
        name: "Commercial Airlock Sector",
        primary: "signal-mask",
        support: ["bio-shield", "containment"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Cargo airlocks and submerged loading bays along a corroded quay.",
        longDescription:
          "Crate hoists stand frozen above black water. The quay edge is a jag of broken concrete and rebar, polished smooth where the tide reaches."
      }
    ],
    hazards: [
      {
        icon: "🌊",
        name: "Flooded Records Sector",
        primary: "reclamation",
        support: ["wild-influence", "transit-anchor"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "critical",
        shortDescription: "Archives drowned to the window line, shelves like ribs under the surface.",
        longDescription:
          "Folders have turned to silt drifts. A rolling ladder floats, bumping the stacks with a hollow rhythm. Light puddles on the water and slides across the ceiling."
      },
      {
        icon: "💨",
        name: "Ventilation Intake Lot",
        primary: "echo-trace",
        support: ["bio-shield", "reclamation"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Banks of rusted intake fans towering over a silted forecourt.",
        longDescription:
          "Fan blades are choked with ropeweed and netting. A metal tag rattles faintly when the wind shifts. The air smells like creosote and tide."
      },
      {
        icon: "📦",
        name: "Freight Access Zone",
        primary: "transit-anchor",
        support: ["safeguard", "signal-mask"],
        baseDuration: 60,
        category: "security",
        difficulty: "standard",
        placement_priority: "high",
        shortDescription: "Collapsed ramps and container rows leading into a drowned terminal.",
        longDescription:
          "Container paint has bled into pastel ghosts. The ramp rails end abruptly in water that hides a second city of debris below."
      },
      {
        icon: "🪸",
        name: "Marine Observation Block",
        primary: "wild-influence",
        support: ["echo-trace", "lock-breach"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Sea-worn platforms and pylons with algae film and broken rails.",
        longDescription:
          "Scope housings point at horizons no one watches. The handrails flake in layers that stain your gloves. Gulls nest in the elbows of old floodlights that will never shine again."
      },
      {
        icon: "🏚️",
        name: "Flooded Living Sector",
        primary: "bio-shield",
        support: ["echo-trace", "lock-breach"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "Lower housing blocks drowned to the window line, interiors slumped inward.",
        longDescription:
          "Mildew blooms on every surface; curtains drift like slow jelly. The line where the tide reaches is written as a salt band around the rooms."
      },
      {
        icon: "🪜",
        name: "Access Tower Sector",
        primary: "safeguard",
        support: ["echo-trace", "lock-breach"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Stair towers and emergency ladders rising above the flood.",
        longDescription:
          "Cages of stairs keep their geometry against the soft line of the water. Every landing clicks and settles when the wind turns."
      },
      {
        icon: "📦",
        name: "Cargo Intake Block",
        primary: "containment",
        support: ["quantum-scan", "reclamation"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "commercial",
        shortDescription: "Shallow-water cargo ramps and corroded conveyors into a warehouse face.",
        longDescription:
          "Conveyor housings gape like fish skulls. A strip of weed waves where a belt used to run. The warehouse doors are heavy with swollen wood."
      },
      {
        icon: "💰",
        name: "Finance Quarter",
        primary: "quantum-scan",
        support: ["containment", "transit-anchor"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Collapsed teller halls and armored delivery bays, empty as vaults.",
        longDescription:
          "Coin-counting machines sit in rows like old teeth. Delivery lanes are silted to their thresholds. Everything smells faintly of metal and mildew."
      }
    ]
  },

  // ======================================================
  "cradle-expanse": {
    name: "Industrial Expanse",
    theme: "Manufacturing grids and rust plains",
    icon: "🌅",
    shortDescription:
      "Endless factory lots, idle conveyors, and yard-wide machinery asleep in rust.",
    longDescription:
      "Production once moved through these blocks in steady lines—ore in one side, finished goods out the other. Now the conveyors are seized and the cranes lean on their cables, frozen mid-motion for generations.\n\nWhen power returns, a servo might twitch or a stack light might glow and die. The wind does most of the work here: sandblasting paint, driving grit into bearings, and covering tracks almost as fast as you make them.",
    artDirection:
      "The one region whose plant stands LIGHTER than its ground: pale umber steel on dark iron-rich dirt, which is the opposite of shard-cities and is what keeps two brown regions apart. Silhouettes are industrial and long — gantry cranes straddling bays, conveyor runs seized where they stopped and carrying on across tiles, bunded tank farms. Rust everywhere, in streaks and pools and oil stains. Almost nothing green: a few dead snags, and the wind has taken the rest.",
    gates: [
      {
        icon: "🏭",
        name: "Factory Command Sector",
        // control rooms and relay racks under gantries, not a checkpoint
        recipe: "infrastructure",
        primary: "system-override",
        support: ["growth-control", "signal-mask"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "critical",
        shortDescription: "Central yard of control rooms and relay racks flanked by gantries.",
        longDescription:
          "Stack lights are long dead above cracked control housings. Catwalks draw hard lines across a sky of dust. A paper logbook lies fused into a solid wedge on a podium."
      },
      {
        icon: "🚛",
        name: "Logistics Yard Gate",
        primary: "drone-command",
        support: ["stealth-transit", "signal-relay"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        shortDescription: "Gatehouse and weighbridge spanning lanes into container lots.",
        longDescription:
          "The weighbridge plate is welded to its frame by corrosion. The gate arms point at the ground like broken fingers. Dust piles where wheels once wore it away."
      },
      {
        icon: "🪖",
        name: "Barracks Gate",
        primary: "signal-mask",
        support: ["signal-relay", "system-override"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Perimeter fence and turnstiles into a worker barracks compound.",
        longDescription:
          "Turnstiles are frozen mid-rotation. A loudspeaker hangs by its cable, a mouth that will never speak. The ground inside is a quilt of footprints long since filled with dust."
      },
      {
        icon: "🍽️",
        name: "Commissary Shutter Sector",
        primary: "system-override",
        support: ["safeguard", "drone-command"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Canteen frontage sealed by roll-down shutters and collapsed awnings.",
        longDescription:
          "Menu boards peel in the heat. Trays clatter when the wind gets under them. At noon the whole block smells like hot metal and old oil."
      }
    ],
    hazards: [
      {
        icon: "⛏️",
        name: "Extraction Yard",
        primary: "drone-command",
        support: ["signal-mask", "stealth-transit"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        shortDescription: "Ore hoppers, crushed drums, and loader tracks etched into dust.",
        longDescription:
          "Chutes hang open over empty ground. Piles of fines drift into dunes against dead conveyors. The only sparkle is mica in the grit."
      },
      {
        icon: "⚙️",
        name: "Assembly Corridor Block",
        primary: "drone-command",
        support: ["wild-influence", "stealth-transit"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Long run of conveyor frames and work platforms seized in place.",
        longDescription:
          "Tool shadows are burned into paint where the sun never reached. Belt rollers are fused in rings of red. The wind writes its own schedule on hanging tags."
      },
      {
        icon: "🗑️",
        name: "Salvage Lot",
        primary: "wild-influence",
        support: ["drone-command", "signal-relay"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Heap of stripped machinery and scaffold bones stacked by a fence.",
        longDescription:
          "Cranes stripped to spines cast laddered shadows. Wire harnesses dangle like dead vines. Footing is uncertain—everything gives a little when you test it."
      },
      {
        icon: "🔧",
        name: "Operations Hub",
        primary: "signal-relay",
        support: ["drone-command", "system-override"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "high_value",
        placement_priority: "critical",
        shortDescription: "Control kiosks and cable troughs forming a nerve-center yard.",
        longDescription:
          "Cable trays cross like irrigation over bare concrete. A signal flag still flaps from a line that never fell. If anything comes alive, it does so in stutters and stops."
      },
      {
        icon: "🔥",
        name: "Boiler Leak Row",
        primary: "safeguard",
        support: ["signal-relay", "system-override"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "Steam-scarred lane between boiler sheds and maintenance stacks.",
        longDescription:
          "Mineral bloom and peeling paint texture every pipe. Valves whistle ghosts when the wind fits their mouths. The ground is white with the memory of heat."
      },
      {
        icon: "🥫",
        name: "Barracks Pantry Depot",
        primary: "stealth-transit",
        support: ["wild-influence", "growth-control"],
        baseDuration: 60,
        category: "storage",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "Food stores and cold-room bays opened to the air long ago.",
        longDescription:
          "Shelving prints squares on the walls where cold once lived. A pyramid of cans has slumped into a gentle hill of dull tin."
      },
      {
        icon: "🏚️",
        name: "Warehouse Racking Collapse",
        primary: "safeguard",
        support: ["drone-command", "system-override"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Racking aisles tumbled into a lattice of unstable paths.",
        longDescription:
          "Number placards hang at wrong angles, their arrows pointing into heaps of beams. The floor is a patchwork of shadows and sunlight where the roof gave up."
      },
      {
        icon: "📒",
        name: "Ledger Block",
        primary: "signal-relay",
        support: ["safeguard", "signal-mask"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Accounting offices and file stores woven into a single crumbling block.",
        longDescription:
          "Adding machines sit like fossils on desks. Ledger paper lies fused into brick-hard stacks. The sun makes dust glitter where a roof panel fell away."
      }
    ]
  },

  // ======================================================
  "hollow-core": {
    name: "Hollow Core",
    theme: "Sublevel transit and collapsed substructures",
    icon: "⚫",
    shortDescription:
      "Buried civic arteries: pillared halls, rail spurs and maintenance blocks, half of them sealed by the collapse.",
    longDescription:
      "Everything aboveground depended on what ran below—transit rings, service corridors, and power trunks stitched together across sublevels. Collapse sealed off some blocks entirely; others breathe stale air through long cracks in the concrete.\n\nOccasionally, indicator strips flare for a moment, enough to show distance receding into black. Then the dark returns, settling even heavier than before.",
    artDirection:
      "A hall, not a street. Pillars on a regular pitch march across every tile and carry on into the next one — that rhythm is the only thing a top-down view has to say there is a ceiling overhead. Rock mass laps in from the corners and outer edges where the roof has come down, sealing parts of blocks, carved back around the rooms and never across a route. Rail beds and cable trays run through. Light is the region's subject: almost none of it, teal bioluminescence for ambient, warm pools under the daylight wells, and the darkness graded so it is heaviest against the rock and lightest over the floor. The concrete itself is NOT black — the palette has to survive the dark and still show which parts are wall.",
    gates: [
      {
        icon: "🚇",
        name: "Sublevel Transit Hub",
        primary: "transit-anchor",
        support: ["signal-relay", "drone-command"],
        baseDuration: 120,
        category: "storage",
        difficulty: "chokepoint",
        placement_priority: "critical",
        shortDescription: "Collapsed interchange where multiple rail spurs once crossed.",
        longDescription:
          "Pillars hold up more air than concrete now. Signal plates stand blank along a bed of gravel and dust. The far wall is a shadow that pretends to be distance."
      },
      {
        icon: "🔌",
        name: "Power Relay Block",
        primary: "signal-relay",
        support: ["quantum-scan", "stealth-transit"],
        baseDuration: 120,
        category: "infrastructure",
        difficulty: "chokepoint",
        placement_priority: "high",
        shortDescription: "Cable trays and junction housings clustered around a maintenance court.",
        longDescription:
          "Insulators line a wall like teeth. A skein of cables has slumped from the ceiling into a black loop. Dust swirls when you step, then remembers how to be still."
      },
      {
        icon: "🚪",
        name: "Habitat Access Sector",
        primary: "lock-breach",
        support: ["quantum-scan", "signal-mask"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Door clusters and service nooks leading into sealed living blocks.",
        longDescription:
          "Numbered arrows march along the wall toward units you’ll never enter. A cart sits on square wheels of dust. The air is dry and old, like a book left open too long."
      },
      {
        icon: "🛒",
        name: "Commerce Access Sector",
        primary: "signal-mask",
        support: ["lock-breach", "echo-trace"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Service corridors feeding into a forgotten plaza and loading cutouts.",
        longDescription:
          "A map board with no map. A mural worn to colors without shapes. The corridor breathes the same cold air in and out, as if thinking of a crowd."
      }
    ],
    hazards: [
      {
        icon: "📦",
        name: "Storage Compound",
        primary: "quantum-scan",
        support: ["lock-breach", "growth-control"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "critical",
        shortDescription: "Box canyons of crates and shelving under a low slab ceiling.",
        longDescription:
          "Label paint lingers like ghosts on metal faces. A broken roller door shows a sliver of stale light. The air tastes like paper and iron."
      },
      {
        icon: "🧱",
        name: "Maintenance Network Sector",
        primary: "growth-control",
        support: ["signal-relay", "drone-command"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Grid of service corridors, tool bays, and grates over dark shafts.",
        longDescription:
          "Tool outlines haunt the walls. Floor grates ring when crossed, answering back from below. The network feels larger than the ground it serves."
      },
      {
        icon: "☢️",
        name: "Reactor Service Lot",
        primary: "stealth-transit",
        support: ["transit-anchor", "quantum-scan"],
        baseDuration: 60,
        category: "power",
        difficulty: "high_value",
        placement_priority: "high",
        shortDescription: "Shield doors and access gantries around a dead core housing.",
        longDescription:
          "Warning chevrons fade across concrete that still remembers heat. Cable stumps jut like bones from panel edges. Everything hums with a silence that feels heavy."
      },
      {
        icon: "🛠️",
        name: "Data Spine Junction",
        primary: "echo-trace",
        support: ["stealth-transit", "signal-mask"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Trunk conduits converge into a node of cracked panels and conduit arcs.",
        longDescription:
          "Cables repeat the curve of the arching ceiling. Labels peel off in satisfying curls. Your headlamp shows dust floating like a slow snowfall."
      },
      {
        icon: "🛗",
        name: "Access Network Block",
        primary: "transit-anchor",
        support: ["echo-trace", "growth-control"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "Lift shafts, catwalks, and ladders crisscrossing a deep chamber.",
        longDescription:
          "Mesh floors draft cool air over your boots. The shaft walls are a history of paint colors layered like sediment. The dark holds its shape until you look into it."
      },
      {
        icon: "🧸",
        name: "Nursery Annex Sector",
        primary: "echo-trace",
        support: ["stealth-transit", "signal-mask"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "Quiet block of sealed playrooms, murals eroded to color fields.",
        longDescription:
          "Small chairs lie on their backs like beetles. A mobile has tangled into a single shape. Everyone left a long time ago, but the rooms still feel paused."
      },
      {
        icon: "📚",
        name: "Records Quarter",
        primary: "stealth-transit",
        support: ["bio-shield", "transit-anchor"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Storage stacks and data cages in a commercial sub-block.",
        longDescription:
          "Cage doors have slumped out of square. A spill of binders lies like fallen bricks. Your light finds the path numbers and makes them legible for a heartbeat."
      },
      {
        icon: "🏬",
        name: "Commerce Plaza",
        primary: "signal-mask",
        support: ["bio-shield", "transit-anchor"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "commercial",
        shortDescription: "Lower concourse ringed by shuttered storefronts and dead kiosks.",
        longDescription:
          "A fountain bowl holds dust instead of water. Kiosk screens are gray mirrors. Every footstep you make has an echo measured in seconds."
      }
    ]
  }
};

// ===============================
// DYNAMIC NAMING SYSTEM (5 options, grounded + region flavor)
// ===============================

// Global brand pools
export const ROOM_NAME_BRANDS = {
  civic: ["CivicSys", "CityWorks", "MetroGrid", "Municipalix", "UrbanNet"],
  industrial: ["StrataDyne", "HelixCore", "OmniForge", "FerrumWorks", "Rivetron"],
  utilities: ["Nextera", "Voltline", "Auralis", "BlueArc", "GridNova"],
  retail: ["MetroTrade", "ApexMart", "Cornerline", "Shoreline Logistics", "Mercantis"],
  bio: ["VerdanCo", "BioNet", "PhytoLabs", "GreenRidge", "GenExis"],
  defense: ["WardTek", "Rampart Systems", "Bastionex", "IronCrest", "Shieldline"],
  data: ["Archivum", "Memora", "DataPlain", "ByteHarbor", "Recorda"],
};

// Region-specific brands
export const ROOM_NAME_REGIONAL_BRANDS = {
  "shard-cities": [
    "MetroGuard",
    "CivicSys",
    "CityWorks",
    "UrbanNet",
    "MetroGrid",
    "ApexLift",
    "Blockline Security",
    "SentraCore",
    "Wayfinder Transit",
    "Arcology Services",
  ],
  "verdant-reclamation": [
    "VerdanCo",
    "PhytoLabs",
    "BioNet",
    "Greencap Utilities",
    "GardenWorks",
    "Nutrivane",
    "IrrigaTech",
    "SeedSafe",
    "Rootline Systems",
    "Auralis Bio",
  ],
  "sunken-bastions": [
    "HarborFleet",
    "DryDock Systems",
    "Bastionex",
    "BlueArc Marine",
    "Deepline",
    "Breakwater Logistics",
    "WardTek Maritime",
    "Pierstone",
    "Tidewell",
    "Seaforth Controls",
  ],
  "cradle-expanse": [
    "FerrumWorks",
    "OmniForge",
    "StrataDyne",
    "Rivetron",
    "Gantry & Co.",
    "Loadstar Fabrication",
    "ConveyorDynamics",
    "IronCrest",
    "Forgeway",
    "MachinaGrid",
  ],
  "hollow-core": [
    "CivicSys Sublevel",
    "MetroGrid Infrastructure",
    "Nextera Utility",
    "ServiceWay",
    "Subterra Transit",
    "CorridorWorks",
    "Spanline",
    "NodeWest",
    "ControlPoint",
    "Concourse Ops",
  ],
};

// Descriptors per category (plus a couple composites)
export const ROOM_NAME_DESCRIPTORS = {
  power: ["Power Node", "Junction Panel", "Substation", "Transformer Bay", "Distribution Rack"],
  storage: ["Storage Bay", "Records Vault", "Supply Depot", "Inventory Room", "Archive Annex"],
  security: ["Security Gate", "Checkpoint", "Access Control", "Barrier Post", "Perimeter Gate"],
  infrastructure: ["Relay Node", "Transit Hub", "Service Corridor", "Maintenance Bay", "Control Room"],
  environmental: ["Containment Zone", "Hazard Control", "Firebreak", "Isolation Area", "Cleanup Sector"],
  wildlife: ["Hive Chamber", "Containment Atrium", "Observation Pen", "Keeper Station", "Handling Room"],
  power_infra: ["Switchgear Room", "Feeder Junction", "Utility Interface", "Load Center", "Breaker Hall"],
  records_data: ["Records Cache", "Data Vault", "Archive Store", "Tape Annex", "Records Room"],
};

// Generic qualifiers + region-specific qualifiers
export const ROOM_NAME_QUALIFIERS = [
  "Block A",
  "Block C",
  "Sector 7",
  "Sector 9",
  "East Annex",
  "West Annex",
  "North Wing",
  "South Wing",
  "Sublevel 2",
  "Sublevel 3",
  "Sublevel 4",
  "Tower 2",
  "Depot Lane",
  "Service Row",
  "Line 3",
  "Bay 12",
  "Bay 17",
  "Midtown Annex",
];

export const ROOM_NAME_REGION_QUALIFIERS = {
  "shard-cities": ["Midtown Annex", "Tower 3", "Sector 9", "Skyway Deck", "Service Row"],
  "verdant-reclamation": ["Greenhouse 2A", "Irrigation Loop B", "Seed Wing", "Containment Hall", "Botany Annex"],
  "sunken-bastions": ["Pier 6", "Drydock 3", "Bulkhead E", "Sublevel Pump 2", "Bilge Corridor"],
  "cradle-expanse": ["Line 3", "Bay 12", "Casting Floor", "Yard 5", "Maintenance Track"],
  "hollow-core": ["Sublevel 4", "Service Spine", "Transit Ring", "Access Spur", "Node J"],
};

// Brand affinity by region/zone/category
export const ROOM_NAME_BRAND_AFFINITY = {
  "shard-cities": ["civic", "utilities", "data", "industrial", "retail", "defense"],
  "verdant-reclamation": ["bio", "utilities", "civic", "industrial", "data"],
  "sunken-bastions": ["defense", "utilities", "industrial", "civic", "data"],
  "cradle-expanse": ["industrial", "utilities", "civic", "retail", "data"],
  "hollow-core": ["civic", "utilities", "industrial", "data", "retail"],
  zone: {
    residential: ["civic", "utilities", "bio", "retail"],
    commercial: ["retail", "civic", "data", "utilities"],
    industrial: ["industrial", "utilities", "defense"],
    security: ["defense", "civic", "utilities"],
    core: ["data", "defense", "industrial"],
    transit: ["civic", "utilities", "industrial"],
    peripheral: ["civic", "industrial", "retail"],
    entry: ["civic", "utilities", "retail"],
  },
  category: {
    power: ["utilities", "industrial", "civic"],
    storage: ["industrial", "retail", "data"],
    security: ["defense", "civic", "utilities"],
    infrastructure: ["civic", "utilities", "industrial"],
    environmental: ["civic", "industrial", "utilities"],
    wildlife: ["bio", "civic", "utilities"],
  },
};

// RNG (seeded)
function xmur3(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^ (h >>> 16)) >>> 0;
  };
}
function mulberry32(a) {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export function makePRNG(seedStr = "") {
  const seed = xmur3(seedStr)();
  return mulberry32(seed);
}
function pick(array, rng) {
  return array[Math.floor(rng() * array.length)];
}

// Brand pool builder with regional weighting
function getBrandPool({ regionKey, zone, category }) {
  const base = ROOM_NAME_BRAND_AFFINITY[regionKey] || ["civic", "industrial", "utilities"];
  const zoneBias = ROOM_NAME_BRAND_AFFINITY.zone[zone] || [];
  const catBias = ROOM_NAME_BRAND_AFFINITY.category[category] || [];
  const order = [...base, ...zoneBias, ...catBias];

  const weighted = [];
  const regional = ROOM_NAME_REGIONAL_BRANDS[regionKey] || [];
  for (let i = 0; i < 3; i++) weighted.push(...regional); // heavy regional weight

  order.forEach((bucket, idx) => {
    const list = ROOM_NAME_BRANDS[bucket] || [];
    const weight = idx < 2 ? 2 : 1;
    for (let i = 0; i < weight; i++) weighted.push(...list);
  });

  return weighted.length ? weighted : [].concat(...Object.values(ROOM_NAME_BRANDS));
}

function getDescriptorPool(category) {
  if (category === "power" && ROOM_NAME_DESCRIPTORS.power_infra) {
    return [...ROOM_NAME_DESCRIPTORS.power, ...ROOM_NAME_DESCRIPTORS.power_infra];
  }
  if (category === "storage" && ROOM_NAME_DESCRIPTORS.records_data) {
    return [...ROOM_NAME_DESCRIPTORS.storage, ...ROOM_NAME_DESCRIPTORS.records_data];
  }
  return ROOM_NAME_DESCRIPTORS[category] || ROOM_NAME_DESCRIPTORS.infrastructure;
}

function pickQualifier(regionKey, rng) {
  const base = ROOM_NAME_QUALIFIERS;
  const regional = ROOM_NAME_REGION_QUALIFIERS[regionKey] || [];
  const pool = [...regional, ...regional, ...base]; // 2:1 favor regional
  return pool.length ? pool[Math.floor(rng() * pool.length)] : "";
}

// Core API: returns 5 grounded, region-flavored name options
export function generateRoomNames(
  {
    baseName, // string e.g. "Housing Power Node"
    regionKey, // one of raidRegions keys
    zone, // "residential" | "commercial" | ...
    category, // "power" | "storage" | "security" | ...
    seed = "", // stable key (coords/roomId)
  },
  count = 5
) {
  const rng = makePRNG(`${seed}|${baseName}|${regionKey}|${zone}|${category}`);
  const brands = getBrandPool({ regionKey, zone, category });
  const descs = getDescriptorPool(category);
  const names = new Set();

  const canon = [baseName, `${baseName} – ${pickQualifier(regionKey, rng)}`];

  let safety = 200;
  while (names.size < count && safety-- > 0) {
    const brand = pick(brands, rng);
    const desc = pick(descs, rng);
    const useBaseTerm = rng() < 0.35; // 35% anchor to base term
    const qualifier = rng() < 0.65 ? ` – ${pickQualifier(regionKey, rng)}` : "";

    const candidate = useBaseTerm ? `${brand} ${baseName}${qualifier}` : `${brand} ${desc}${qualifier}`;

    names.add(candidate);
  }

  for (const c of canon) {
    if (names.size >= count) break;
    names.add(c);
  }
  return Array.from(names).slice(0, count);
}

// ===============================
// RENDER CONFIG (consumed by js/modules/roomImageGen.js)
// ===============================
// Pure JSON-compatible data describing how tiles are drawn — no functions.
// Designing a new room look means editing config here, not render code.
//
// regions.<key>: per-region theme
//   backdrop — floor treatment (city|greenhouse|harbor|foundry|underground)
//   overlay  — weathering pass (dust|vines|flood|rust|dark)
//   tune     — knobs, merged over defaultTune:
//     buildingScale — multiplier on roof footprints (bigger blocks)
//     treeDensity   — multiplier on the vegetation scatter counts
//     roadWear      — 0..1 degradation of road connectors
//     shadowAngle/shadowAlpha/shadowLength — static shadow direction,
//       darkness, and max dawn/dusk offset (animated)
//     lightUptime / lightFlickerSpeed — intermittent powered lights
//     sinkholeChance — environmental rooms: sinkhole vs overgrowth
//     lightRays     — daylight-well density (underground regions only)
//     cloudType     — cloud preset name (see CLOUD_TYPES in the renderer)
//   pal      — palette; keys are referenced by name from recipes
//
// recipes.<key>: what a room type draws, interpreted by the renderer.
//   Either { elements: [...] } or { variants: [{ elements }, ...] } —
//   variants are an even seeded pick, or gated by a tune knob when
//   variantKey is set (variants[1] is chosen when rng < tune[variantKey]).
//   Recipe-level settings (all optional):
//     (roads are not a recipe concern — they come from the generated road
//      network stamped on each room; see roadNetwork-core.js)
//     treeDensity: n   — vegetation multiplier for this room type, stacked
//                        on the region's tune.treeDensity (default 1)
//   Each element spec: { el: '<registry name>', ...params } plus optional
//     chance: 0..1     — probability the element appears
//     repeat: n|[a,b]  — draw n times, params re-rolled each time
//     when: { tune, gte } — only when a tune knob reaches a threshold
//   Param values: scalar, [min,max] range, or { pick: [v, v, ...] }.
//   Rooms in this file may also set `recipe: '<name>'` to override the
//   name-keyword selection entirely.
export const RENDER_CONFIG = {
  defaultTune: {
    buildingScale: 1, treeDensity: 1, roadWear: 0.15,
    shadowAngle: 45, shadowAlpha: 0.85, shadowLength: 6,
    lightUptime: 0.12, lightFlickerSpeed: 1,
    sinkholeChance: 0.5, lightRays: 0,
    // How often a region's own signature props appear — the one pass that is
    // about a PARTICULAR place rather than about a town. 0 turns them off.
    signature: 1,
    // How far inland the sea reaches on a region with a shoreline. 0 = no sea,
    // which is every region but the drowned one.
    floodLevel: 0,
    // How much rock mass has come down around the built ground. Sublevels only.
    enclosure: 0,
    cloudType: 'scattered',
  },

  regions: {
    // Two tiers of architecture per region. `struct` is the ordinary building
    // stock — the sheds, blocks and terraces the whole map is made of. `mass`
    // is the monumental tier: the towers, glasshouses, sea walls, mill frames
    // and pillar halls a region builds at a scale nothing else on the tile
    // reaches. Keeping them apart is what lets shard-cities have warm brown
    // roofs on its small buildings AND cool concrete over them, instead of
    // picking one and reading as somewhere else's town in a filter.
    //
    // The rule both tiers answer to: a structure must separate from the ground
    // it stands on in VALUE, not just in hue. Two regions with the same value
    // relationship look like the same place no matter what colour they are.
    'shard-cities': {
      backdrop: 'city', overlay: 'dust',
      tune: { buildingScale: 1.3, treeDensity: 3, roadWear: 0.35, shadowAngle: 45, shadowAlpha: 0.85, shadowLength: 6, lightUptime: 0.18, lightFlickerSpeed: 1, sinkholeChance: 0.5, lightRays: 0, signature: 1, cloudType: 'broken' },
      pal: {
        far: '#1c2333',
        // Warm brick and brown roofing, but a stop LIGHTER than it used to be
        // and over darker tarmac. The hue contrast against grey ground always
        // worked; the value contrast did not — seven luma points apart, the
        // roofs sank into the street at map scale and the region read as one
        // undifferentiated grey-brown field.
        struct: '#8a705c', structDark: '#15171e', structLight: '#b99983',
        mass: '#79828f', massDark: '#2a3038', massLight: '#a9b6c4',
        ground1: '#3f434e', ground2: '#4b5160', road: '#20242f',
        metal: '#6b7893', rust: '#8a6a4a', accent: '#a9bee0',
        veg1: '#3b5841', veg2: '#5d8058', water1: '#33455c',
        water2: '#55708c', glow: '#ffb45e',
      },
    },
    'verdant-reclamation': {
      backdrop: 'greenhouse', overlay: 'vines',
      recipes: { storage: 'storage-seed' },
      tune: { buildingScale: 1.1, treeDensity: 4, roadWear: 0.75, cloudType: 'scattered', lightUptime: 0.05, signature: 1.1 },
      pal: {
        far: '#4c6350',
        struct: '#7d8a8c', structDark: '#455256', structLight: '#b0bcba',
        mass: '#9fb0a8', massDark: '#4a5a52', massLight: '#cfdcd2',
        // The ground is most of the tile, so it decides whether the region reads
        // as flat — and a light mid-green under light grey frames was the whole
        // problem. Taken down to shaded loam, but only halfway: the first attempt
        // went the full distance and the region lost its light instead of gaining
        // contrast. The canopy is darker still, the structures sit well above
        // both, and the tile has a range without going gloomy.
        ground1: '#3e5a34', ground2: '#4f5b30', road: '#42402f',
        metal: '#8b9598', rust: '#8a7050', accent: '#d8d2a8',
        // Deep canopy. These are the darkest greens on the tile and the shapes
        // they make are what the eye reads first, so they carry the contrast.
        veg1: '#254d2a', veg2: '#4a7a3a', water1: '#3f7a70', water2: '#6fb0a4',
        glow: '#f4e9c8',
      },
    },
    'sunken-bastions': {
      backdrop: 'harbor', overlay: 'flood',
      recipes: { storage: 'storage-quay' },
      tune: { buildingScale: 1.15, treeDensity: 1, roadWear: 0.4, cloudType: 'overcast', sinkholeChance: 0.35, floodLevel: 1, signature: 1 },
      pal: {
        far: '#3a4854',
        struct: '#6d8290', structDark: '#2c3841', structLight: '#9db6c2',
        mass: '#7d8f99', massDark: '#2c3841', massLight: '#a8bcc4',
        ground1: '#4a5450', ground2: '#5b6763', road: '#39434c',
        metal: '#7b8a93', rust: '#b06a3d', accent: '#8fc4cc',
        veg1: '#3c5f4c', veg2: '#58825e', water1: '#2f5c6b', water2: '#5a8f9b',
        glow: '#ffd27f',
      },
    },
    'cradle-expanse': {
      backdrop: 'foundry', overlay: 'rust',
      recipes: { storage: 'storage-silo' },
      tune: { buildingScale: 1.25, treeDensity: 0.8, roadWear: 0.3, shadowAngle: 15, cloudType: 'wispy', signature: 1 },
      pal: {
        far: '#4a3226',
        // The one region whose plant is LIGHTER than its ground. Dark dirt with
        // pale steel on it is the opposite value relationship to shard-cities'
        // dark roofs on grey tarmac, which is what stops two brown regions
        // reading as one.
        struct: '#a5825c', structDark: '#3a2417', structLight: '#d2ad7f',
        mass: '#a89279', massDark: '#4a3a28', massLight: '#d3c0a4',
        ground1: '#6b5137', ground2: '#7d6244', road: '#3d3226',
        metal: '#9c8a6e', rust: '#b55a2b', accent: '#e8b060',
        veg1: '#635831', veg2: '#807236', water1: '#5d564a', water2: '#7a705c',
        glow: '#ffcf7a',
      },
    },
    'hollow-core': {
      backdrop: 'underground', overlay: 'dark',
      // Everything here is lifted a stop from where it was: a sublevel is dark
      // because of its LIGHT, not because its concrete is black. The palette
      // has to survive a x0.5 night tint and a darkness wash on top and still
      // show which parts of the tile are wall.
      tune: { buildingScale: 1.1, treeDensity: 1, roadWear: 0.25, lightRays: 1.5, enclosure: 1, signature: 1.2 },
      pal: {
        far: '#07080c',          // the void below a chasm — must stay near-black
        struct: '#5c6782', structDark: '#1b1f2a', structLight: '#7f8cad',
        mass: '#6b7690', massDark: '#1b1f2a', massLight: '#909dbb',
        ground1: '#333a46', ground2: '#454d5d', road: '#1c212b',
        metal: '#5b6478', rust: '#5a4630', accent: '#4fa0a0',
        veg1: '#2d473f', veg2: '#41684e', water1: '#234049', water2: '#356470',
        glow: '#ffc46a',
      },
    },
  },

  recipes: {
    power: { variants: [
      { elements: [
        { el: 'transformerPad', x: [9, 21], y: [9, 21] },
        { el: 'transformerPad', x: [84, 93], y: [84, 93] },
        { el: 'rect', x: 30, y: [24, 30], w: 3, h: 60, color: 'structDark' },   // trunk cable
        { el: 'rect', x: 30, y: 87, w: [51, 60], h: 3, color: 'structDark' },
        { el: 'fence', x: 6, y: 6, w: 36, h: 36 },
      ] },
      { elements: [
        { el: 'tank', x: [24, 33], y: [24, 33], r: 18 },                          // fuel tank + feed pipes
        { el: 'pipeline', x: 6, y: [87, 96], w: 108 },
        { el: 'transformerPad', x: [84, 93], y: [15, 24] },
      ] },
    ] },

    storage: { variants: [
      { elements: [
        { el: 'crates', x: [9, 15], y: [9, 18], cols: 3, rows: 2 },
        { el: 'crates', x: [78, 87], y: [78, 87], cols: 2, rows: 2 },
        { el: 'tank', x: [90, 99], y: [21, 30], r: 15 },                         // silo top
        { el: 'building', x: [9, 15], y: [78, 84], w: 27, h: 24 },                // warehouse shed
      ] },
      { elements: [
        { el: 'container', x: [9, 18], y: [9, 15], vert: 0 },                    // container yard
        { el: 'container', x: [9, 18], y: [27, 33], vert: 0 },
        { el: 'container', x: [81, 93], y: [75, 84], vert: 1 },
        { el: 'crates', x: [15, 24], y: [81, 90], cols: 2, rows: 2 },
      ] },
    ] },

    // ---- region-specific stock ----
    // A region may point one of the shared recipes at its own layout via
    // `recipes` on its RENDER_CONFIG.regions entry. The signature layer adds a
    // region's landmarks ON TOP of whatever a tile drew; this changes what the
    // tile is made of in the first place, which is the difference between a
    // harbour with containers on it and a harbour built out of them.
    //
    // Kept deliberately few. Every one of these is a second layout to maintain
    // for a recipe that already had one, so it has to earn its place by being
    // somewhere the shared version reads plainly wrong.
    'storage-quay': { variants: [
      { elements: [                                                              // stacked container rows
        { el: 'container', x: [9, 15], y: [12, 18], vert: 0 },
        { el: 'container', x: [9, 15], y: [27, 33], vert: 0 },
        { el: 'container', x: [9, 15], y: [42, 48], vert: 0 },
        { el: 'container', x: [78, 90], y: [69, 81], vert: 1 },
        { el: 'crates', x: [66, 75], y: [15, 24], cols: 2, rows: 2 },
      ] },
      { elements: [                                                              // transit shed on the quay
        { el: 'building', x: [15, 24], y: [15, 24], w: 60, h: 27 },
        { el: 'container', x: [15, 27], y: [78, 87], vert: 0 },
        { el: 'container', x: [60, 72], y: [75, 84], vert: 1 },
      ] },
    ] },
    'storage-silo': { variants: [
      { elements: [                                                              // the silo farm
        { el: 'silo', x: [21, 27], y: [24, 30], r: 13 },
        { el: 'silo', x: [21, 27], y: [69, 75], r: 11 },
        { el: 'silo', x: [72, 81], y: [45, 54], r: 14 },
        { el: 'pipeline', x: 33, y: [51, 57], w: [30, 39] },
      ] },
      { elements: [                                                              // hoppers and a loading shed
        { el: 'silo', x: [78, 87], y: [24, 33], r: 12 },
        { el: 'tank', x: [24, 33], y: [21, 30], r: 15 },
        { el: 'building', x: [15, 24], y: [72, 81], w: 42, h: 24 },
        { el: 'crates', x: [72, 84], y: [78, 90], cols: 2, rows: 2 },
      ] },
    ] },
    'storage-seed': { variants: [
      { elements: [                                                              // seed store under glass
        { el: 'glassSpan', x: [12, 18], y: [15, 24], w: 66, h: 27 },
        { el: 'silo', x: [24, 30], y: [78, 87], r: 11 },
        { el: 'silo', x: [54, 63], y: [81, 90], r: 9 },
        { el: 'crates', x: [84, 93], y: [69, 78], cols: 2, rows: 2 },   // pallets in the yard
      ] },
      { elements: [
        { el: 'silo', x: [18, 24], y: [21, 30], r: 12 },
        { el: 'glassSpan', x: [63, 72], y: [21, 30], w: 33, h: 66 },
        { el: 'crates', x: [15, 24], y: [78, 87], cols: 3, rows: 2 },
      ] },
    ] },

    security: { elements: [
      { el: 'fence', x: 15, y: 15, w: 90, h: 90, gaps: 1 },
      { el: 'barrier', x: 24, y: 24 },
      { el: 'barrier', x: 81, y: 81 },
      { el: 'vehicle', chance: 0.4, x: [72, 84], y: [78, 87], vert: { pick: [0, 1] } }, // patrol wreck
      { el: 'building', x: [72, 81], y: [21, 30], w: 18, h: 15 },                 // guard post
    ] },

    infrastructure: { variants: [
      { elements: [
        { el: 'building', x: [9, 15], y: [9, 15], w: [36, 45], h: [27, 33] },
        { el: 'pipeline', x: 6, y: [87, 96], w: 108 },                          // surface pipe run
        { el: 'rect', x: [81, 93], y: [15, 24], w: 15, h: 15, color: 'metal' },    // vent pad
      ] },
      { elements: [
        { el: 'tower', x: [15, 24], y: [15, 24] },                                 // mast + cable spans
        { el: 'rect', x: 24, y: 30, w: 3, h: [54, 66], color: 'structDark' },
        { el: 'rect', x: 24, y: 90, w: [54, 66], h: 3, color: 'structDark' },
        { el: 'tank', x: [84, 93], y: [81, 90], r: 12 },
        { el: 'rect', x: [81, 93], y: [15, 24], w: 15, h: 15, color: 'metal' },
      ] },
    ] },

    transit: { elements: [
      { el: 'rect', x: [18, 30], y: [72, 84], w: 6, h: 27, color: 'struct' },    // fallen girders
      { el: 'rect', x: [78, 90], y: [15, 27], w: 27, h: 6, color: 'struct' },
      { el: 'vehicle', x: [12, 24], y: [12, 24], vert: { pick: [0, 1] } },         // abandoned wrecks
      { el: 'vehicle', chance: 0.6, x: [78, 90], y: [78, 90], vert: { pick: [0, 1] } },
      { el: 'rubble', x: 12, y: 12, w: 93, h: 93, n: 12 },
    ] },

    environmental: { variantKey: 'sinkholeChance', variants: [
      { elements: [                                                            // overgrowth
        { el: 'crack', repeat: 4, x: [9, 105], y: [9, 96], len: [18, 36] },
        { el: 'rubble', x: 6, y: 6, w: 105, h: 105, n: 18 },
        { el: 'foliage', x: [15, 36], y: [78, 102], size: [4, 6] },
        { el: 'foliage', x: [81, 102], y: [15, 36], size: [4, 6] },
        { el: 'tree', x: [81, 99], y: [81, 99], r: [9, 12] },
      ] },
      { elements: [                                                            // sinkhole
        // The hole goes down LAST. Scatter drawn over it is debris hanging in
        // a shaft; drawn under it, the hole simply swallows whatever fell in
        // and what is left reads as ground that gave way. The pit brings its
        // own spoil ring with it, so the rim still has a heap on it.
        { el: 'rubble', x: 12, y: 12, w: 93, h: 93, n: 14 },
        { el: 'sinkhole', x: [36, 78], y: [36, 78] },
      ] },
    ] },

    records: { variants: [
      { elements: [
        { el: 'building', x: [12, 18], y: [12, 18], w: [36, 42], h: [27, 33] },
        { el: 'building', when: { tune: 'buildingScale', gte: 1.15 }, x: [78, 87], y: [12, 18], w: 24, h: 21 },
        { el: 'speckle', x: 6, y: 6, w: 105, h: 105, n: 8, color: 'structLight', alpha: 0.8 }, // scattered paper
      ] },
      { elements: [
        { el: 'building', x: [12, 18], y: [12, 18], w: [36, 42], h: [27, 33] },
        { el: 'building', x: [78, 84], y: [78, 84], w: [24, 30], h: [21, 27] },
        { el: 'building', when: { tune: 'buildingScale', gte: 1.15 }, x: [78, 87], y: [12, 18], w: 24, h: 21 },
        { el: 'speckle', x: 6, y: 6, w: 105, h: 105, n: 8, color: 'structLight', alpha: 0.8 },
      ] },
      // The two above put a hall in each corner, which leaves a road nowhere to
      // go: mirroring the layout only moves which corner is which. These two
      // range the halls along ONE edge instead, so half the tile stays open —
      // and the mirror decides which half, to suit wherever the road landed.
      // Ranged east-west, for a road crossing that way:
      { elements: [
        { el: 'building', x: [9, 15], y: 9, w: [45, 54], h: [27, 33] },
        { el: 'building', x: [66, 72], y: 9, w: [33, 39], h: [24, 30] },
        { el: 'speckle', x: 6, y: 6, w: 105, h: 105, n: 8, color: 'structLight', alpha: 0.8 },
      ] },
      // and ranged north-south, for one crossing the other way:
      { elements: [
        { el: 'building', x: 9, y: [9, 15], w: [27, 33], h: [42, 48] },
        { el: 'building', x: 9, y: [66, 72], w: [24, 30], h: [33, 39] },
        { el: 'speckle', x: 6, y: 6, w: 105, h: 105, n: 8, color: 'structLight', alpha: 0.8 },
      ] },
    ] },

    habitation: { variants: [
      { elements: [
        { el: 'building', x: [9, 15], y: [9, 15], w: [30, 36], h: [24, 30] },
        { el: 'building', x: [78, 84], y: [12, 18], w: [27, 33], h: [21, 27] },
        { el: 'building', x: [12, 18], y: [81, 87], w: [27, 33], h: [21, 27] },
        { el: 'building', when: { tune: 'buildingScale', gte: 1.15 }, x: [81, 87], y: [81, 87], w: 24, h: 21 },
        { el: 'rect', repeat: 4, x: [78, 105], y: [81, 105], w: 6, h: 6, color: 'veg1' }, // planters
        { el: 'bush', x: [81, 102], y: [18, 36] },
      ] },
      { elements: [
        { el: 'building', x: [9, 15], y: [9, 15], w: [42, 48], h: [36, 42] },    // one large block
        { el: 'tree', x: [84, 96], y: [15, 27], r: 9 },
        { el: 'bush', x: [81, 102], y: [18, 36] },
      ] },
      // Housing is the one thing that is genuinely built in rows, so two of
      // them: one long range and a shorter one behind it, facing the other way
      // as a back street does. The gap between is the lane.
      { elements: [
        { el: 'terrace', x: [9, 15], y: [9, 15], w: [54, 69], h: [21, 27] },
        { el: 'terrace', chance: 0.8, x: [15, 27], y: [69, 75], w: [42, 57], h: [21, 24] },
        { el: 'bush', x: [84, 102], y: [45, 63] },
      ] },
      // and the same row stood on end, so a mirror is not the only way this
      // recipe ever runs north-south
      { elements: [
        { el: 'terrace', x: [9, 15], y: [9, 15], w: [21, 27], h: [54, 69], vert: 1 },
        { el: 'terrace', chance: 0.8, x: [69, 75], y: [15, 27], w: [21, 24], h: [42, 57], vert: 1 },
        { el: 'bush', x: [45, 63], y: [84, 102] },
      ] },
    ] },

    commerce: { variants: [
      { elements: [
        { el: 'storefront', x: [12, 18], y: [9, 15], w: [54, 66], h: [27, 33] },
        { el: 'stalls' },                                                      // market stalls
      ] },
      { elements: [
        { el: 'storefront', x: [12, 18], y: [9, 15], w: [54, 66], h: [27, 33] },
        { el: 'crates', x: [78, 90], y: [75, 87], cols: 2, rows: 2 },           // stock left on the lot
      ] },
      // A parade: shops in a row under one roof, with the lot they served in
      // front of them. One storefront alone reads as a retail park; the row is
      // what a high street is.
      //
      // Both ways round, and both shallower than the storefronts above, because
      // this is the only thing in the recipe that fits on a tile the road runs
      // through. A carriageway swings up to EDGE_SWING off centre, so the
      // clear ground beside one is about 43 units — and a storefront is 46 to
      // 49 deep once the region's scale and its awning are on it. It does not
      // fit in either orientation, in any mirror, which left a market tile on a
      // road as bare ground and some crates. A range 27 to 35 deep clears the
      // carriageway with room to spare, and the north-south copy does the same
      // for a road running the other way, which no mirror of an east-west range
      // can do. The layout pass prefers whichever of the five keeps a structure
      // standing, so a tile with no road through it still gets its big shop.
      { elements: [
        { el: 'terrace', x: [9, 15], y: [9, 12], w: [57, 72], h: [21, 27] },
        { el: 'crates', chance: 0.5, x: [15, 27], y: [78, 87], cols: 2, rows: 2 },
      ] },
      { elements: [
        { el: 'terrace', x: [9, 12], y: [9, 15], w: [21, 27], h: [57, 72], vert: 1 },
        { el: 'crates', chance: 0.5, x: [78, 87], y: [15, 27], cols: 2, rows: 2 },
      ] },
      // A corner unit, for where not even a parade reaches. A bend sweeps an L
      // through the tile and leaves a quarter of it clear, which nothing that
      // has to span an axis can use — so the smallest thing this recipe can be
      // is three shops in a corner rather than no shop at all.
      { elements: [
        { el: 'terrace', x: [9, 15], y: [9, 15], w: [24, 30], h: [18, 24] },
        { el: 'stalls', chance: 0.5 },
      ] },
    ] },

    reactor: { elements: [{ el: 'reactorCore' }] },

    // Entry and extraction. A portal is a DEVICE somebody installed and powers,
    // so it stands in a facility rather than out in the open: ranges of
    // blockwork around three sides of the aperture, a transformer feeding it,
    // and the pad itself left clear in the middle (portalPad and portalGlow
    // both draw at the tile centre, and the glow goes down last of all).
    //
    // The buildings sit in one corner-half so the layout pass can mirror them
    // clear of the track that runs up to the tile — every mirror leaves two
    // adjacent sides open, which between them covers whichever edge the road
    // arrives from.
    portal: { elements: [
      { el: 'building', x: 9, y: 9, w: [48, 57], h: [21, 27] },      // north range
      { el: 'building', x: 9, y: [36, 42], w: [21, 27], h: [39, 45] }, // west range
      { el: 'transformerPad', x: [84, 93], y: [12, 18] },            // what powers it
      { el: 'crates', x: [78, 87], y: [72, 81], cols: 2, rows: 2 },
      // a deck under the aperture, so it reads as installed rather than as
      // something that happened to appear in the yard
      { el: 'rect', x: 36, y: 36, w: 48, h: 48, color: 'structDark' },
      { el: 'rect', x: 39, y: 39, w: 42, h: 42, color: 'ground2' },
      { el: 'portalPad' },
    ] },

    road: { elements: [
      // Debris on the carriageway goes down FIRST, so the frontage below covers
      // what lands on its ground instead of speckling its roofs.
      { el: 'rubble', x: 6, y: 6, w: 105, h: 105, n: 4 },
      // Frontage. What makes a trunk read as a STREET rather than as a strip of
      // surfacing is the building line either side of it — and the recipe is
      // never told which way the road goes, so it puts a block against all four
      // edges and lets the roadway pick. The layout pass suppresses whatever
      // the road runs through: on a straight run that is the two edges it
      // enters and leaves by, leaving the other two facing each other across
      // the carriageway; on a bend it is the two sides it turns between,
      // leaving the outside of the corner built up. Same rule, no cases.
      //
      // Depth is set so a block stands where a road CAN reach and is dropped
      // when it does. A crossing swings up to EDGE_SWING (24) off centre, so
      // shrinking these until they always survive would leave a strip of open
      // ground between every building and the road it faces — a setback that
      // varies tile to tile and reads as waste. Deep blocks sit tight against
      // the carriageway instead, and where the road hugs one side that side's
      // block goes and the far one stands alone, which is what a street pushed
      // against a boundary actually looks like.
      //
      // The far pair is anchored at 80 rather than hard against the edge, and
      // the near pair at 8: a building is clamped 6 units short of the tile
      // edge, and the layout pass MIRRORS it, so one authored against the edge
      // is measured clamped and then drawn somewhere it is not — bigger than
      // the footprint it was cleared on, with the difference landing on the
      // road. Authored sizes are multiplied by buildingScale (1.3 at most), so
      // 80 + 24 x 1.3 = 111 keeps every one of these inside the clamp at both
      // ends of its mirror, and the two passes agree.
      //
      // Terraced, because a STREET is what this recipe is for. A free-standing
      // block set back behind a carriageway is a depot; a run of units flush
      // along it, sharing party walls, is a street, and the difference is the
      // whole reason a trunk tile exists. The run is authored to the same 8/80
      // anchors and the same depth as the blocks it replaces, so the clamp
      // arithmetic above is unchanged; only what stands on the frontage is.
      { el: 'terrace', chance: 0.85, x: [12, 21], y: 8, w: [45, 57], h: 24 },
      { el: 'terrace', chance: 0.85, x: [12, 21], y: 80, w: [45, 57], h: 24 },
      { el: 'terrace', chance: 0.85, x: 8, y: [12, 21], w: 24, h: [45, 57], vert: 1 },
      { el: 'terrace', chance: 0.85, x: 80, y: [12, 21], w: 24, h: [45, 57], vert: 1 },
      { el: 'manhole', chance: 0.5, x: [48, 72], y: [48, 72] },
      // Traffic is not in here: drawRoadside lays it against the carriageway off
      // the road's own samples, so it lies parallel to the road and on the
      // shoulder, on every tile the network runs through rather than only on
      // the trunk tiles this recipe draws.
      { el: 'streetLamp', x: { pick: [39, 78] }, y: [39, 81] },
    ] },

    // rest stops are untouched clearings, and they
    // sit in the greenest spots on the map (5x the region's tree density)
    water: { treeDensity: 5, elements: [{ el: 'pond' }] },

    campsite: { treeDensity: 5, elements: [
      { el: 'tent', x: [15, 24], y: [15, 24] },
      { el: 'fireRing', x: [78, 90], y: [78, 90] },
      { el: 'rect', x: [18, 30], y: [84, 96], w: 18, h: 6, color: 'rust' },      // bedroll
      { el: 'rect', x: [81, 96], y: [18, 27], w: 6, h: 15, color: 'structDark' }, // supply post
    ] },

    // Impassable because the ground itself has gone, not because something fell
    // over on it: a shaft or a rift opened here and there is no way across.
    // Collapsed slabs read as debris you could pick your way through, which is
    // exactly what these tiles are not.
    // The hole goes down LAST in every variant. Ground litter drawn over it is
    // debris hanging in mid-air above a drop; drawn under it, the hole swallows
    // whatever fell in and what survives reads as the edge of the collapse.
    // Each hole lays its own spoil around its rim afterwards.
    blocked: { variants: [
      { elements: [                                                            // a shaft
        { el: 'crack', repeat: 4, x: [9, 105], y: [9, 96], len: [21, 36] },
        { el: 'rubble', x: 6, y: 6, w: 105, h: 105, n: 18 },
        { el: 'sinkhole', x: [51, 69], y: [51, 69], r: [33, 39] },
        { el: 'darkWash', alpha: 0.15 },
      ] },
      { elements: [                                                            // a rift, east-west
        { el: 'crack', repeat: 3, x: [9, 105], y: [9, 96], len: [21, 36] },
        { el: 'rubble', x: 6, y: 6, w: 105, h: 105, n: 18 },
        { el: 'chasm', x: [54, 66], y: [48, 72], len: [96, 108], r: [18, 24] },
        { el: 'darkWash', alpha: 0.15 },
      ] },
      { elements: [                                                            // and north-south
        { el: 'crack', repeat: 3, x: [9, 105], y: [9, 96], len: [21, 36] },
        { el: 'rubble', x: 6, y: 6, w: 105, h: 105, n: 18 },
        { el: 'chasm', x: [48, 72], y: [54, 66], len: [96, 108], r: [18, 24], vert: 1 },
        { el: 'darkWash', alpha: 0.15 },
      ] },
    ] },
  },

  // ===============================
  // SKILL ANCHORS
  // ===============================
  // A recipe says what kind of place a tile IS; the anchor says what the team
  // will be doing there. One per skill, chosen from the room's primary skill
  // and drawn ON TOP of the recipe — a power yard still reads as a power yard
  // and also shows its skill. Every skill is the primary of exactly four rooms
  // (see tests/data/skillBalance.test.js), so all fifteen of these are reachable.
  //
  // Same element vocabulary and same interpreter as recipes above, with two
  // differences: coordinates are PLATE-RELATIVE (0,0 is the anchor's own top
  // left, not the tile's), and w/h declare the reserved footprint, which must
  // stay within ANCHOR_MAX (24) so the anchor can never land on the connector
  // cross that joins tiles together.
  //
  // Every entry opens with an anchorPlate: it makes an anchor that overlaps a
  // roof read as apparatus installed there, which is what buys us out of doing
  // collision detection against the recipe. shape marks the faction — square
  // for Dominion, cut for Veil, round for Wilders.
  //
  // The anchor tracks the room's PRIMARY skill, not the event it will run —
  // and the two can disagree, because a room's event pool is primary plus
  // support. That is deliberate: a room runs one to four events, so keying the
  // anchor to any single one of them would mismatch the others regardless. The
  // primary is the only skill true for the whole room. (What does follow the
  // pinned event is the roof cutaway — see the environment field on events.)
  skillAnchors: {
    // ---- Dominion: systems and instrumentation ----
    'signal-relay': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24 },
      { el: 'tower', x: 4, y: 4 },                                  // guyed lattice mast
    ] },
    'system-override': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24 },
      { el: 'rect', x: 6, y: 5, w: 12, h: 11, color: 'struct' },    // console pedestal
      { el: 'rect', x: 7, y: 6, w: 10, h: 1, color: 'structLight' },
      { el: 'rect', x: 8, y: 8, w: 8, h: 5, color: 'accent' },      // lit screen face
      { el: 'rect', x: 11, y: 16, w: 2, h: 5, color: 'structDark' },// cable to the deck edge
    ] },
    'drone-command': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24 },
      { el: 'dronePad', x: 4, y: 4, s: 16 },
    ] },
    'safeguard': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24 },
      { el: 'transformerPad', x: 2, y: 2 },                         // fenced transformer, 21x21
    ] },
    'quantum-scan': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24 },
      { el: 'dish', x: 12, y: 12, r: 7, rings: 1, arm: 1, color: 'accent' },
    ] },

    // ---- Veil: access and concealment ----
    'lock-breach': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'cut' },
      { el: 'rect', x: 4, y: 4, w: 16, h: 16, color: 'metal' },     // armoured hatch slab
      { el: 'rect', x: 4, y: 4, w: 16, h: 1, color: 'structLight' },
      { el: 'rect', x: 4, y: 19, w: 16, h: 1, color: 'structDark' },
      // hazard stripe: a bare metal square reads as nothing from above, and
      // this is the one thing that separates a sealed door from a floor plate
      { el: 'rect', x: 4, y: 5, w: 16, h: 2, color: 'accent', alpha: 0.85 },
      { el: 'manhole', x: 12, y: 13 },                              // wheel lock
      { el: 'rect', x: 3, y: 8, w: 2, h: 3, color: 'structDark' },  // hinges
      { el: 'rect', x: 3, y: 15, w: 2, h: 3, color: 'structDark' },
    ] },
    'stealth-transit': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'cut' },
      { el: 'rect', x: 5, y: 5, w: 3, h: 14, color: 'structDark' }, // sensor gate posts
      { el: 'rect', x: 16, y: 5, w: 3, h: 14, color: 'structDark' },
      { el: 'rect', x: 5, y: 6, w: 3, h: 1, color: 'metal' },
      { el: 'rect', x: 16, y: 6, w: 3, h: 1, color: 'metal' },
      { el: 'rect', x: 8, y: 11, w: 8, h: 2, color: 'accent', alpha: 0.7 }, // the beam
    ] },
    'echo-trace': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'cut' },
      { el: 'dish', x: 12, y: 12, r: 3, rings: 2, color: 'accent' },// flush emitter + ripples
    ] },
    'signal-mask': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'cut' },
      { el: 'rect', x: 6, y: 9, w: 12, h: 9, color: 'struct' },     // jammer box
      { el: 'rect', x: 6, y: 9, w: 12, h: 1, color: 'structLight' },
      { el: 'rect', x: 11, y: 2, w: 1, h: 8, color: 'metal' },      // whip antenna
      { el: 'speckle', x: 3, y: 3, w: 18, h: 18, n: 10, color: 'accent', alpha: 0.35 }, // hash
    ] },
    'transit-anchor': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'cut' },
      { el: 'crack', x: 4, y: 6, len: 14 },                         // the fissure
      { el: 'rect', x: 4, y: 10, w: 16, h: 5, color: 'rust' },      // pinned gangway
      { el: 'rect', x: 4, y: 10, w: 16, h: 1, color: 'structLight' },
      { el: 'rect', x: 4, y: 9, w: 2, h: 7, color: 'metal' },       // pins
      { el: 'rect', x: 18, y: 9, w: 2, h: 7, color: 'metal' },
    ] },

    // ---- Wilders: growth, salvage and containment ----
    'reclamation': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'round' },
      { el: 'tank', x: 12, y: 13, r: 8 },                           // harvest vat
      { el: 'foliage', x: 16, y: 8, size: 3 },                      // biomass over the rim
    ] },
    'growth-control': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'round' },
      { el: 'rect', x: 4, y: 5, w: 16, h: 14, color: 'structDark' },// framed grow bed
      { el: 'rect', x: 5, y: 6, w: 14, h: 12, color: 'ground1' },
      { el: 'rect', x: 6, y: 8, w: 12, h: 2, color: 'veg1' },       // seeded rows
      { el: 'rect', x: 6, y: 12, w: 12, h: 2, color: 'veg2' },
      { el: 'rect', x: 6, y: 16, w: 12, h: 1, color: 'veg1' },
    ] },
    'wild-influence': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'round' },
      { el: 'dish', x: 12, y: 13, r: 7, rings: 1, color: 'veg2' },  // pheromone bloom
      { el: 'rect', x: 11, y: 2, w: 2, h: 5, color: 'structDark' }, // beacon post
    ] },
    'bio-shield': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'round' },
      { el: 'pipeline', x: 2, y: 6, w: 20 },                        // scrubber feed
      { el: 'tank', x: 15, y: 15, r: 6 },
    ] },
    'containment': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'round' },
      { el: 'fence', x: 2, y: 2, w: 20, h: 20 },                    // quarantine plot
      { el: 'barrier', x: 3, y: 17 },                               // striped barrier, 18x6
    ] },
  },
};
